import React from 'react';
import {
  AtSign,
  Bell,
  CheckCircle,
  FileUp,
  FileX,
  Gauge,
  Hand,
  Mail,
  MailOpen,
  MessageSquare,
  Reply,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  Ticket,
  TicketPlus,
  UserPlus,
} from 'lucide-react';

/**
 * Plan-NotificationsHeaderPopover.md §7 — shared between the header popover
 * (NotificationsPopover) and the in-Inbox tab (NotificationsTab). Kept in
 * sync with the backend `Notification.type` enum; default branch falls back
 * to a generic Bell so a freshly-added type never breaks rendering before
 * the FE catches up.
 */
export const getIconForType = (type: string): React.ReactNode => {
  switch (type) {
    // Tickets
    case 'ticket_created':
      return <TicketPlus className="w-5 h-5 text-blue-500" />;
    case 'ticket_assigned':
      return <Ticket className="w-5 h-5 text-blue-500" />;
    case 'ticket_comment':
      return <MessageSquare className="w-5 h-5 text-blue-400" />;
    case 'ticket_mention':
      return <AtSign className="w-5 h-5 text-blue-500" />;

    // Emails
    case 'email_received':
      return <MailOpen className="w-5 h-5 text-purple-500" />;
    case 'email_reply':
      return <Reply className="w-5 h-5 text-purple-400" />;
    case 'email_assigned':
      return <Mail className="w-5 h-5 text-purple-500" />;

    // Roles & Team
    case 'role_requested':
      return <ShieldAlert className="w-5 h-5 text-amber-500" />;
    case 'role_approved':
      return <ShieldCheck className="w-5 h-5 text-green-500" />;
    case 'team_invite_pending':
      return <UserPlus className="w-5 h-5 text-amber-500" />;
    case 'team_invite_accepted':
      return <CheckCircle className="w-5 h-5 text-green-500" />;

    // Knowledge
    case 'knowledge_upload':
      return <FileUp className="w-5 h-5 text-teal-500" />;
    case 'knowledge_summary_ready':
    case 'knowledge_split_ready':
    case 'knowledge_ai_description_ready':
      return <Sparkles className="w-5 h-5 text-teal-500" />;
    case 'knowledge_summary_failed':
      return <FileX className="w-5 h-5 text-red-500" />;
    case 'help_center_generated':
      return <Sparkles className="w-5 h-5 text-violet-500" />;
    case 'help_center_generation_failed':
      return <FileX className="w-5 h-5 text-red-500" />;
    case 'layout_chat_complete':
      return <Sparkles className="w-5 h-5 text-indigo-500" />;
    case 'layout_chat_failed':
      return <FileX className="w-5 h-5 text-red-500" />;

    // Contacts
    case 'contact_auto_saved':
      return <UserPlus className="w-5 h-5 text-indigo-500" />;

    // Playground collaboration
    case 'playground_comment':
      return <MessageSquare className="w-5 h-5 text-indigo-500" />;

    // Human-agent handoff — amber matches the requested-banner color
    // used in ChatsTab so the visual language is consistent.
    case 'human_handoff_requested':
      return <Hand className="w-5 h-5 text-amber-500" />;

    // Billing — pooled AI-text-token thresholds. 🚨 ONE backend type covers
    // all three rungs (80 / 95 / 100%): `maybe_send_text_pool_threshold_alert`
    // sends the highest threshold crossed and puts the rung in
    // `metadata.threshold`, so there is no separate "exhausted" type to case
    // on. Severity is carried by the backend's own title ("AI text allowance
    // used up") and by the red exhausted state on TextPoolBar.
    //
    // 🚨 The `billing.` prefix is load-bearing — every billing notification is
    // namespaced (`billing.payment_failed`, `billing.bundle_purchased`, …)
    // while the older ticket/email types are not. Dropping it silently falls
    // through to the generic Bell and the '#' link.
    case 'billing.text_pool_threshold':
      return <Gauge className="w-5 h-5 text-amber-500" />;

    default:
      return <Bell className="w-5 h-5 text-gray-500" />;
  }
};

/**
 * Canonical route for a notification type. Backend may override per-row
 * via `metadata.link` (e.g. playground_comment's session-specific deep
 * link); callers should prefer `metadata.link ?? getLinkForType(type)`.
 *
 * Email types route to `/inbox` rather than `'#'` here so the popover —
 * which can't trigger the Inbox tab's `onEmailNotificationClick` handler —
 * still lands the user in the right place. NotificationsTab calls
 * `preventDefault()` and routes via the handler for email_* types, so the
 * `to=` value is functionally dead there.
 */
export const getLinkForType = (type: string): string => {
  switch (type) {
    case 'ticket_created':
    case 'ticket_assigned':
    case 'ticket_comment':
    case 'ticket_mention':
      return '/tickets';
    case 'email_received':
    case 'email_reply':
    case 'email_assigned':
      return '/inbox';
    case 'role_requested':
    case 'role_approved':
    case 'team_invite_pending':
    case 'team_invite_accepted':
      return '/team';
    case 'contact_auto_saved':
      return '/contacts';
    case 'knowledge_upload':
    case 'knowledge_summary_ready':
    case 'knowledge_summary_failed':
    case 'knowledge_split_ready':
    case 'knowledge_ai_description_ready':
    case 'help_center_generated':
    case 'help_center_generation_failed':
      return '/knowledge';
    case 'layout_chat_complete':
    case 'layout_chat_failed':
      return '/help-center';
    case 'playground_comment':
      return '/playground';
    case 'human_handoff_requested':
      return '/inbox';
    // 🚨 The emitter's metadata carries threshold/used/allowance/period but NO
    // `link`, so without this case the row falls to '#' and is unclickable.
    case 'billing.text_pool_threshold':
      return '/billing';
    default:
      return '#';
  }
};
